import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  Alert,
} from '@mui/material';
import {
  yesNoOptions,
  jids,
} from '../../../../../data/forms/generalDropdownOptions.js';
import AdditionalItemsForm from './additionalItemsForm.js';
import axios from 'axios';
import CopyToClipboardModal from './CopyToClipboardModal.js';

const BenchRepairForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    openCase: '',
    oem: '',
    rrfRepair: '',
    caseNumber: '',
    rmaNumber: '',
    comments: '',
    email: user.email,
    emailAddress: user.email,
    userId: user.userId,
    displayName: user.displayName,
  });

  const [entries, setEntries] = useState([
    {
      id: 1,
      product: '',
      itemNumber: '',
      itemNumberOptions: [],
      itemRrfSupported: true,
      assetTag: '',
      serialNumber: '',
      resolution: '',
      failureMode: '',
      solution: [],
      partsUsed: [],
      isRepeatReturn: '',
      returnDate: null,
      oemVendor: '',
      totalWorkTime: '',
      status: '',
      startDate: null,
      completionDate: null,
      comments: '',
    },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [repairOrderModalOpen, setRepairOrderModalOpen] = useState(false);
  const [repairOrderToCopy, setRepairOrderToCopy] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    if (name === 'openCase' && value !== 'Yes') {
      setForm(prevForm => ({
        ...prevForm,
        caseNumber: '',
      }));
    } else if (name === 'oem' && value !== 'Yes') {
      setForm(prevForm => ({
        ...prevForm,
        rmaNumber: '',
      }));
      setEntries(prevEntries =>
        prevEntries.map(entry => ({
          ...entry,
          oemVendor: '',
        }))
      );
    } else if (name === 'jurisdiction' && value !== 'RRF') {
      setEntries(prevEntries =>
        prevEntries.map(entry => ({
          ...entry,
          isRepeatReturn: '',
          returnDate: null,
        }))
      );
    }
  };

  const handleClear = () => {
    setForm({
      jurisdiction: '',
      openCase: '',
      oem: '',
      rrfRepair: '',
      caseNumber: '', // renders conditionally on Open Case == Yes
      rmaNumber: '', // renders conditionally on oem == Yes
      comments: '',
    });

    setEntries(prevEntries =>
      prevEntries.map(entry => ({
        ...entry,
        product: '',
        itemNumber: '',
        itemNumberOptions: [],
        itemRrfSupported: true,
        rmaNumber: '',
        assetTag: '',
        serialNumber: '',
        resolution: '',
        failureMode: '',
        solution: [],
        partsUsed: [],
        isRepeatReturn: '',
        returnDate: null,
        oemVendor: '',
        totalWorkTime: '',
        status: '',
        startDate: null,
        completionDate: null,
        comments: '',
      }))
    );
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/benchrepair/submit`,
        data: {
          ...form,
          additionalEntries: entries,
        },
      });

      const payload = response?.data ?? {};
      setRepairOrderToCopy(
        payload.repairOrder != null && String(payload.repairOrder).trim() !== ''
          ? String(payload.repairOrder).trim()
          : ''
      );
      setRepairOrderModalOpen(true);
      setIsSubmitting(false);
      // Do not clear form or reset parent selection here — that unmounts this
      // component and the Repair Order modal would disappear immediately.
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const handleCloseRepairOrderModal = () => {
    setRepairOrderModalOpen(false);
    setRepairOrderToCopy('');
    handleClear();
    handleFormSelectionReset();
  };

  const hasUnsupportedRrfItem =
    form.rrfRepair === 'Yes' &&
    entries.some(entry => entry.itemNumber && entry.itemRrfSupported === false);

  const isFormValid = () => {
    const { jurisdiction, openCase, oem } = form;

    if (!jurisdiction || !openCase || !oem) {
      return false;
    }

    if (hasUnsupportedRrfItem) {
      return false;
    }

    for (let entry of entries) {
      const {
        product,
        itemNumber,
        serialNumber,
        resolution,
        failureMode,
        isRepeatReturn,
        returnDate,
        status,
        startDate,
        totalWorkTime,
        oemVendor,
        partsUsed,
      } = entry;

      if (
        !product ||
        !itemNumber ||
        !serialNumber ||
        !resolution ||
        !failureMode ||
        !status ||
        !startDate ||
        !totalWorkTime
      ) {
        return false;
      }

      if (partsUsed.length === 0) {
        return false;
      }

      if (oem === 'Yes' && !oemVendor) {
        return false;
      }

      if (jurisdiction === 'RRF' && !isRepeatReturn) {
        return false;
      }

      if (jurisdiction === 'RRF' && isRepeatReturn === 'Yes' && !returnDate) {
        return false;
      }
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 6, md: 4 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Select Form Type'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Select Form Type'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jids.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Open Case'}</InputLabel>
            <Select
              value={form.openCase}
              label={'Open Case'}
              onChange={handleChange}
              name='openCase'
            >
              {yesNoOptions.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'OEM'}</InputLabel>
            <Select
              value={form.oem}
              label={'OEM'}
              onChange={handleChange}
              name='oem'
            >
              {yesNoOptions.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'RRF Repair'}</InputLabel>
            <Select
              value={form.rrfRepair}
              label={'RRF Repair'}
              onChange={handleChange}
              name='rrfRepair'
            >
              {yesNoOptions.map(option => (
                <MenuItem value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Tooltip
            title={
              form.openCase !== 'Yes'
                ? "Please select 'Yes' on Open Case to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.openCase !== 'Yes'}
              id='outlined-required'
              label='Case Number'
              name='caseNumber'
              fullWidth
              value={form.caseNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <Tooltip
            title={
              form.oem !== 'Yes'
                ? "Please select 'Yes' on OEM to enable this field"
                : ''
            }
            arrow
          >
            <TextField
              disabled={form.oem !== 'Yes'}
              id='outlined-required'
              label='RMA Number'
              name='rmaNumber'
              fullWidth
              value={form.rmaNumber}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            />
          </Tooltip>
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label='Comments'
            name='comments'
            value={form.comments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <AdditionalItemsForm
          mainForm={form}
          entries={entries}
          setEntries={setEntries}
          setSnackbarMessage={setSnackbarMessage}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarSeverity={setSnackbarSeverity}
        />
        {hasUnsupportedRrfItem && (
          <Grid size={12}>
            <Alert severity='warning'>
              Product(s) are not supported by the RRF
            </Alert>
          </Grid>
        )}
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              hasUnsupportedRrfItem
                ? 'Product(s) are not supported by the RRF'
                : !isFormValid()
                  ? 'Please fill in all required fields to enable the Submit button'
                  : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      <CopyToClipboardModal
        open={repairOrderModalOpen}
        onClose={handleCloseRepairOrderModal}
        value={repairOrderToCopy}
        title='Repair Order Number'
        label='Repair Order Number'
        emptyValueText='N/A'
        autoCloseMs={10000}
        onCopySuccess={msg => {
          setSnackbarMessage(msg || 'Copied!');
          setSnackbarSeverity('success');
          setIsSnackbarOpen(true);
        }}
        onCopyError={() => {
          setSnackbarMessage(
            'Unable to copy Repair Order Number. Please copy manually.',
          );
          setSnackbarSeverity('error');
          setIsSnackbarOpen(true);
        }}
      />
    </>
  );
};

export default BenchRepairForm;
